/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import type {
  FormattedContentStackLandingPage,
  ContentStackPdpSlot,
  SizePerDevice,
  ContentStackPost,
} from '@/features/contentstack/interface';
import { CMSFaq } from '@/features/contentstack/components/CMSFaq/CMSFaq';
import { CMSBannerSplit } from '@/features/contentstack/components/CMSBannerSplit/CMSBannerSplit';
import { CMSDealsSplit } from '@/features/contentstack/components/CMSDealsSplit/CMSDealsSplit';
import {
  CMSSpacer,
  type CMSSpacerProps,
} from '@/features/contentstack/components/CMSSpacer/CMSSpacer';
import { CMSMenuList } from '@/features/contentstack/components/CMSMenuList/CMSMenuList';
import { CMSYmme } from '@/features/contentstack/components/CMSYmme/CMSYmme';
import { CMSImageCategory } from '@/features/contentstack/components/CMSImageCategory/CMSImageCategory';
import { CMSBannerCTA } from '@/features/contentstack/components/CMSBannerCTA/CMSBannerCTA';
import { CMSLogoDescription } from '@/features/contentstack/components/CMSLogoDescription/CMSLogoDescription';
import { CMSCategoryGrid } from '@/features/contentstack/components/CMSCategoryGrid/CMSCategoryGrid';
import { CMSImageContent } from '@/features/contentstack/components/CMSImageContent/CMSImageContent';
import { CMSImageContentBackground } from '@/features/contentstack/components/CMSImageContentBackground/CMSImageContentBackground';
import { CMSGrid } from '@/features/contentstack/components/CMSGrid/CMSGrid';
import { CMSPreFooter } from '@/features/contentstack/components/CMSPreFooter/CMSPreFooter';
import { CMSBlogGrid } from '@/features/contentstack/components/CMSBlogGrid/CMSBlogGrid';
import { CMSProductTypeGrid } from '@/features/contentstack/components/CMSProductTypeGrid/CMSProductTypeGrid';
import { CMSRichText } from '@/features/contentstack/components/CMSRichText/CMSRichText';
import { CMSFeaturedContent } from '@/features/contentstack/components/CMSFeaturedContent/CMSFeaturedContent';
import { CMSVideoContent } from '@/features/contentstack/components/CMSVideoContent/CMSVideoContent';
import { CMSProductChart } from '@/features/contentstack/components/CMSProductChart/CMSProductChart';
import { CMSBanner } from '@/features/contentstack/components/CMSBanner/CMSBanner';
import { CMSButtonGrid } from '@/features/contentstack/components/CMSButtonGrid/CMSButtonGrid';
import { CMSTarget } from '@/features/contentstack/components/CMSTarget/CMSTarget';
import { CMSExperienceInjector } from '@/features/contentstack/components/CMSExperienceInjector/CMSExperienceInjector';
import { CMSImageVideoSplit } from '@/features/contentstack/components/CMSImageVideoSplit/CMSImageVideoSplit';
import { CMSHeading } from '@/features/contentstack/components/CMSHeading/CMSHeading';
import { CMSIconGrid } from '@/features/contentstack/components/CMSIconGrid/CMSIconGrid';
import { CMSCertona } from '@/features/contentstack/components/CMSCertona/CMSCertona';
import { isValidCMSImageContent } from '@/features/contentstack/utils/isValidCMSImageContent';
import { STARCTypography } from '@/components/STARCTypography';
import dynamic from 'next/dynamic';
import { routePaths } from '@/constants/routePaths';
import { RewardsActivity } from '@/features/rewards/components/RewardsActivity/RewardsActivity';
import { useRewardDetails } from '@/features/rewards/api/getRewardDetails';
import { useRewardsActivity } from '@/features/rewards/api/getRewardsActivity';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useScrollIntoViewDOM } from '@/hooks/useScrollIntoView';
import { SuperDuperHero } from '@/features/home/components/SuperDuperHero/SuperDuperHero';
import { type CertonaBlockProps, type CertonaRecommendation } from '@/features/certona';
import { CMSGridBanner } from '@/features/contentstack/components/CMSGridBanner/CMSGridBanner';
import CMSTermsAndConditions from '@/features/contentstack/components/CMSTermsAndConditions/CMSTermsAndConditions';
import { CMSVinDecoder } from '@/features/contentstack/components/CMSVinDecoder/CMSVinDecoder';
import { CMSIframe } from '@/features/contentstack/components/CMSIframe/CMSIframe';
import { CMSPencilBanner } from '@/features/contentstack/components/CMSPencilBanner/CMSPencilBanner';
import { useAppData } from '@/hooks/useAppData';
import { getCMSModularBlockId } from '@/features/contentstack/utils/getCMSModularBlockId';
import MegaHero from '@/features/contentstack/components/CMSMegaHero/MegaHero';

const LazyCMSCircularPDF = dynamic(
  () =>
    import('@/features/contentstack/components/CMSCircularPDF/CMSCircularPDF').then(
      (mod) => mod.CMSCircularPDF,
    ),
  {
    ssr: false,
  },
);

const LazyMembershipStatusBlock = dynamic(() =>
  import('@/components/ContentBlocks/MembershipStatusBlock').then(
    (mod) => mod.MembershipStatusBlock,
  ),
);

const rewardsLoggedInSizes: SizePerDevice = {
  mobile: {
    width: 704,
    height: 1089,
  },
  tablet: {
    width: 2200,
    height: 764,
  },
  desktop: {
    width: 3120,
    height: 773,
  },
};

const CMSLPSpacer = ({ children, ...rest }: { id?: string } & CMSSpacerProps) => {
  const router = useLegacyRouter();
  const isNotPDP = router.route !== '/products/[...slug]';
  return (
    <CMSSpacer landingPage={isNotPDP} {...rest}>
      {children}
    </CMSSpacer>
  );
};

type PostContent = NonNullable<ContentStackPost['post_content']>[number];
type LpPostContent = NonNullable<PostContent['landingpage_component']>['component'];

type Props = {
  certonaRecommendations?: CertonaRecommendation[];
  content:
    | FormattedContentStackLandingPage['modularBlocks']
    | ContentStackPdpSlot['pdp_slot_content']
    | LpPostContent;
  isTopContent?: boolean;
  setCertonaRecommendationAddedToCart?: React.Dispatch<React.SetStateAction<boolean>>;
} & CertonaBlockProps;

export const CMSModularHandler = ({
  content,
  certonaRecommendations,
  setCertonaRecommendationAddedToCart,
  isTopContent,
  ...certonaProps
}: Props) => {
  const router = useLegacyRouter();
  const { data: rewardsDetails } = useRewardDetails();
  const { appData } = useAppData();
  const isLoggedIn = appData.userAuthenticated;

  const isRewardsLP =
    router.asPath === routePaths.rewardsLandingPage ||
    router.asPath.includes(`${routePaths.rewardsLandingPage}?`);

  const shouldFetchRewardsActivity =
    router.isReady && isLoggedIn && Boolean(rewardsDetails?.loyaltyCardNumber) && isRewardsLP;

  const { data: rewardsData } = useRewardsActivity({
    enabled: shouldFetchRewardsActivity,
  });

  useScrollIntoViewDOM();

  return (
    <STARCTypography>
      {content?.map((modularBlock, i) => {
        const getBlockId = (blockName: string) => getCMSModularBlockId(modularBlock, blockName);
        if ('faq' in modularBlock && modularBlock.faq?.faq?.[0]?.faq?.length) {
          return (
            <CMSLPSpacer key={i} id={getBlockId('faq')}>
              <CMSFaq content={modularBlock.faq.faq[0]} />
            </CMSLPSpacer>
          );
        }

        if (modularBlock.image_category_grid?.image_category_grid?.[0]) {
          return (
            <CMSLPSpacer key={i} id={getBlockId('image_category_grid')}>
              <CMSImageCategory content={modularBlock.image_category_grid.image_category_grid[0]} />
            </CMSLPSpacer>
          );
        }

        if (modularBlock.banner?.banner?.[0]?.asset_size) {
          return (
            <CMSLPSpacer key={i} id={getBlockId('banner')}>
              <CMSBanner
                content={modularBlock.banner.banner[0]}
                priority={isTopContent && i === 0}
                sizePerDevice={rewardsLoggedInSizes}
              />
            </CMSLPSpacer>
          );
        }

        if (modularBlock.banner_split?.banner_split?.[0]) {
          return (
            <CMSLPSpacer key={i} id={getBlockId('banner_split')}>
              <CMSBannerSplit content={modularBlock.banner_split.banner_split[0]} />
            </CMSLPSpacer>
          );
        }

        if (modularBlock.deals_split?.deals_split?.[0]) {
          return (
            <CMSLPSpacer key={i} id={getBlockId('deals_split')}>
              <CMSDealsSplit content={modularBlock.deals_split?.deals_split[0]} />
            </CMSLPSpacer>
          );
        }

        if ('ymme' in modularBlock && modularBlock.ymme?.ymme?.[0]) {
          return (
            <CMSLPSpacer key={i} id={getBlockId('ymme')}>
              <CMSYmme content={modularBlock.ymme.ymme[0]} nonShopPage={true} />
            </CMSLPSpacer>
          );
        }

        if (modularBlock.image_content_background?.image_content_background?.[0]) {
          return (
            <CMSLPSpacer key={i} id={getBlockId('image_content_background')}>
              <CMSImageContentBackground
                content={modularBlock.image_content_background.image_content_background[0]}
              />
            </CMSLPSpacer>
          );
        }
        if (modularBlock.logo_description?.logo_description?.[0]) {
          return (
            <CMSLPSpacer key={i} id={getBlockId('logo_description')}>
              <CMSLogoDescription content={modularBlock.logo_description.logo_description[0]} />
            </CMSLPSpacer>
          );
        }

        if (modularBlock.banner_cta?.banner_cta?.[0]) {
          return (
            <CMSLPSpacer key={i} id={getBlockId('banner_cta')}>
              <CMSBannerCTA
                content={modularBlock.banner_cta.banner_cta[0]}
                priority={isTopContent && i === 0}
              />
            </CMSLPSpacer>
          );
        }

        if ('menu_list' in modularBlock && modularBlock.menu_list?.menu_list?.[0]) {
          return (
            <CMSLPSpacer key={i} id={getBlockId('menu_list')}>
              <CMSMenuList content={modularBlock.menu_list.menu_list[0]} />
            </CMSLPSpacer>
          );
        }

        if (
          'super_duper_hero' in modularBlock &&
          modularBlock.super_duper_hero?.super_duper_hero?.[0]
        ) {
          return (
            <CMSLPSpacer
              key={`${i}-hero`}
              removeHorizontalSpacing
              id={getBlockId('super_duper_hero')}
            >
              <SuperDuperHero options={modularBlock.super_duper_hero.super_duper_hero[0]} />
            </CMSLPSpacer>
          );
        }

        if ('mega_hero' in modularBlock && modularBlock.mega_hero?.mega_hero?.[0]) {
          return (
            <CMSLPSpacer
              key={`${i}-mega-hero`}
              removeHorizontalSpacing
              removeVerticalSpacing
              id={getBlockId('mega_hero')}
            >
              <MegaHero options={modularBlock.mega_hero.mega_hero[0]} />
            </CMSLPSpacer>
          );
        }

        if ('target_div' in modularBlock && modularBlock.target_div?.target_div?.[0]) {
          return (
            <CMSLPSpacer key={i} id={getBlockId('target_div')}>
              <CMSTarget content={modularBlock.target_div.target_div[0]} />
            </CMSLPSpacer>
          );
        }

        if ('heading' in modularBlock && modularBlock.heading?.heading?.[0]) {
          return (
            <CMSLPSpacer key={i} id={getBlockId('heading')}>
              <CMSHeading key={i} content={modularBlock.heading.heading[0]} />
            </CMSLPSpacer>
          );
        }

        if (
          'experience_injector' in modularBlock &&
          modularBlock.experience_injector?.experience_injector?.[0]
        ) {
          return (
            <CMSLPSpacer key={i} id={getBlockId('experience_injector')}>
              <CMSExperienceInjector
                content={modularBlock.experience_injector.experience_injector[0]}
              />
            </CMSLPSpacer>
          );
        }

        if (
          'image_video_split' in modularBlock &&
          modularBlock.image_video_split?.image_video_split?.[0]
        ) {
          return (
            <CMSLPSpacer key={i} id={getBlockId('image_video_split')}>
              <CMSImageVideoSplit content={modularBlock.image_video_split.image_video_split[0]} />
            </CMSLPSpacer>
          );
        }

        if ('grid_banner' in modularBlock && modularBlock.grid_banner?.grid_banner?.[0]) {
          return (
            <CMSLPSpacer key={i} id={getBlockId('grid_banner')}>
              <CMSGridBanner content={modularBlock.grid_banner.grid_banner[0]} />
            </CMSLPSpacer>
          );
        }

        if (modularBlock.category_grid?.category_grid?.[0]) {
          return (
            <CMSLPSpacer key={i} id={getBlockId('category_grid')}>
              <CMSCategoryGrid content={modularBlock.category_grid.category_grid[0]} />
            </CMSLPSpacer>
          );
        }

        if (
          modularBlock.image_content?.image_content?.[0] &&
          isValidCMSImageContent(modularBlock.image_content?.image_content?.[0])
        ) {
          return (
            <CMSLPSpacer key={i} id={getBlockId('image_content')}>
              <CMSImageContent content={modularBlock.image_content.image_content[0]} />
            </CMSLPSpacer>
          );
        }

        if (modularBlock.button_grid?.button_grid?.[0]) {
          return (
            <CMSLPSpacer key={i} removeHorizontalSpacing id={getBlockId('button_grid')}>
              <CMSButtonGrid content={modularBlock.button_grid.button_grid[0]} />
            </CMSLPSpacer>
          );
        }

        if (modularBlock.parttype_grid?.parttype_grid?.[0]) {
          return (
            <CMSLPSpacer key={i} id={getBlockId('parttype_grid')}>
              <CMSGrid content={modularBlock.parttype_grid.parttype_grid[0]} />
            </CMSLPSpacer>
          );
        }

        if ('prefooter' in modularBlock && modularBlock.prefooter?.prefooter?.[0]) {
          return (
            <CMSLPSpacer key={`${i}-prefooter`} id={getBlockId('prefooter')}>
              <CMSPreFooter content={modularBlock.prefooter.prefooter[0]?.column} />
            </CMSLPSpacer>
          );
        }
        if (modularBlock.product_grid?.product_grid?.[0]) {
          return (
            <CMSLPSpacer key={`${i}-product_grid`} id={getBlockId('product_grid')}>
              <CMSProductTypeGrid
                content={modularBlock.product_grid.product_grid[0]}
                gridIndex={i}
              />
            </CMSLPSpacer>
          );
        }

        if (modularBlock.blogtype_grid?.blogtype_grid?.[0]) {
          return (
            <CMSLPSpacer key={i} id={getBlockId('blogtype_grid')}>
              <CMSBlogGrid content={modularBlock.blogtype_grid.blogtype_grid[0]} />
            </CMSLPSpacer>
          );
        }

        if (modularBlock.rich_text_editor?.rich_text_editor?.[0]) {
          const contentBackground =
            modularBlock.rich_text_editor?.rich_text_editor?.[0].description_background;
          const contentHasBackground = !!contentBackground && contentBackground !== 'None';

          return (
            <CMSLPSpacer
              key={`${i}-rich-text`}
              removeHorizontalSpacing={contentHasBackground}
              id={getBlockId('rich_text_editor')}
            >
              <CMSRichText content={modularBlock.rich_text_editor.rich_text_editor[0]} />
            </CMSLPSpacer>
          );
        }
        if (modularBlock.product_chart?.product_chart?.[0]) {
          return (
            <CMSLPSpacer key={i} id={getBlockId('product_chart')}>
              <CMSProductChart content={modularBlock.product_chart.product_chart[0]} />
            </CMSLPSpacer>
          );
        }

        if (modularBlock.featured_content?.featured_content?.[0]) {
          return (
            <CMSLPSpacer key={i} id={getBlockId('featured_content')}>
              <CMSFeaturedContent content={modularBlock.featured_content.featured_content[0]} />
            </CMSLPSpacer>
          );
        }
        if (modularBlock.video_content?.video_content?.[0]) {
          return (
            <CMSLPSpacer key={`${i}-rich-text`} id={getBlockId('video_content')}>
              <CMSVideoContent content={modularBlock.video_content.video_content[0]} />
            </CMSLPSpacer>
          );
        }

        if ('membership_status' in modularBlock && modularBlock.membership_status?.enable) {
          if (isLoggedIn && rewardsDetails?.loyaltyCardNumber) {
            return (
              <CMSLPSpacer key={`${i}-membership`} id={getBlockId('membership_status')}>
                <LazyMembershipStatusBlock isFromCMS />
              </CMSLPSpacer>
            );
          }
        }

        if ('rewards_activity' in modularBlock && modularBlock.rewards_activity?.enable) {
          if (
            isLoggedIn &&
            rewardsDetails?.loyaltyCardNumber &&
            rewardsData &&
            rewardsData.rewardsActivityLineItems.length > 0
          ) {
            return (
              <CMSLPSpacer key={`${i}-rewards-activity`} id={getBlockId('rewards_activity')}>
                <RewardsActivity rewardsData={rewardsData} />
              </CMSLPSpacer>
            );
          }
        }

        if ('icon_grid' in modularBlock && modularBlock.icon_grid?.icon_grid?.[0]) {
          return (
            <CMSLPSpacer key={`${i}-icon-grid`} id={getBlockId('icon_grid')}>
              <CMSIconGrid content={modularBlock.icon_grid.icon_grid?.[0]} />
            </CMSLPSpacer>
          );
        }

        if ('circular_pdf' in modularBlock && modularBlock.circular_pdf?.circular_pdf?.[0]) {
          return (
            <CMSLPSpacer key={i} id={getBlockId('circular_pdf')}>
              <LazyCMSCircularPDF content={modularBlock.circular_pdf.circular_pdf[0]} />
            </CMSLPSpacer>
          );
        }

        if ('certona' in modularBlock && modularBlock.certona?.certona?.[0]) {
          return (
            <CMSLPSpacer key={i} id={getBlockId('certona')}>
              <CMSCertona
                content={modularBlock.certona.certona[0]}
                certonaRecommendations={certonaRecommendations}
                onCertonaAddToCartClick={() => {
                  setCertonaRecommendationAddedToCart?.(true);
                }}
                {...certonaProps}
              />
            </CMSLPSpacer>
          );
        }

        if (
          'terms_conditions' in modularBlock &&
          modularBlock.terms_conditions?.terms_conditions?.[0]
        ) {
          return (
            <CMSLPSpacer key={i} id={getBlockId('terms_conditions')}>
              <CMSTermsAndConditions
                content={modularBlock.terms_conditions?.terms_conditions?.[0]}
              />
            </CMSLPSpacer>
          );
        }

        if ('vin_lookup' in modularBlock && modularBlock.vin_lookup?.enable) {
          return (
            <CMSLPSpacer key={`${i}-vin-lookup`} id={getBlockId('vin_lookup')}>
              <CMSVinDecoder />
            </CMSLPSpacer>
          );
        }

        if ('iframe' in modularBlock && modularBlock.iframe?.iframe?.[0].source_url) {
          return (
            <CMSLPSpacer key={`${i}-iframe`} id={getBlockId('iframe')}>
              <CMSIframe content={modularBlock.iframe.iframe[0]} />
            </CMSLPSpacer>
          );
        }

        if ('pencil_banner' in modularBlock && modularBlock.pencil_banner?.pencil_banner?.[0]) {
          return (
            <CMSLPSpacer
              key={modularBlock.pencil_banner.pencil_banner[0].uid}
              {...(modularBlock.pencil_banner.pencil_banner[0].component_id && {
                id: modularBlock.pencil_banner.pencil_banner[0].component_id,
              })}
            >
              <CMSPencilBanner content={modularBlock.pencil_banner?.pencil_banner?.[0]} />
            </CMSLPSpacer>
          );
        }

        return null;
      })}
    </STARCTypography>
  );
};
